import { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Select from "react-select";
import {
  SKOR_TICKET_CATEGORIES,
  DOCUMENT_REQUIRED_CATEGORIES,
} from "./skorTicketCategories";
import { FiPaperclip, FiUser, FiX } from "react-icons/fi";
import useTranslator from "../../hooks/useTranslator";
import axiosInstance from "../../services/authWrapper";
import { createSkorTicket } from "../../services/skorTicketService";
import { getUserDisplayName, toast } from "../../helper/common";
import constants from "../../Utils/constants";
import styles from "./SkorTicket.module.css";

const ACCEPTED_FILE_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const getCompulsoryDocNames = (tenant) => {
  const docsByStatus = constants.PROFESSION_DOCS ?? {};
  if (tenant?.employment_status === "student") {
    return Object.keys(docsByStatus.STUDENT_DOCS ?? {});
  }
  if (tenant?.employment_status === "employed") {
    return Object.keys(
      tenant?.employment_category === constants.domestic_worker
        ? (docsByStatus.DOMESTIC_WORKER_DOCS ?? {})
        : (docsByStatus.EMPLOYED_DOCS ?? {})
    );
  }
  if (
    tenant?.employment_status === "self_employed" &&
    tenant?.employment_category === "freelancer"
  ) {
    return Object.keys(docsByStatus.FREELANCER_DOCS ?? {});
  }
  if (
    tenant?.employment_status === "self_employed" &&
    tenant?.employment_category === "ownACompany"
  ) {
    return Object.keys(docsByStatus.SELF_EMPLOYED_DOCS ?? {});
  }
  if (tenant?.employment_status === "retired") {
    return Object.keys(docsByStatus.RETIRED_DOCS ?? {});
  }
  if (tenant?.employment_status === "unemployed") {
    return Object.keys(docsByStatus.UNEMPLOYED_DOCS ?? {});
  }
  if (tenant?.employment_status === "public_servant") {
    return Object.keys(docsByStatus.PUBLIC_SERVANT_DOCS ?? {});
  }
  if (tenant?.employment_status === "company") {
    return Object.keys(docsByStatus.COMPANY_DOCS ?? {});
  }
  return [];
};

export default function CreateSkorTicketModal({
  tenant,
  isGuarantor,
  onClose,
}) {
  const { _t } = useTranslator();
  const [linkedTenantId, setLinkedTenantId] = useState(tenant?._id || null);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("medium");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documents, setDocuments] = useState(tenant?.tenantDocs || []);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentLoadFailed, setDocumentLoadFailed] = useState(false);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const submitting = useRef(false);
  const fileInput = useRef(null);
  const canUnlinkProfile = localStorage.getItem("usertype") === "landlord";
  const categorySettings = SKOR_TICKET_CATEGORIES.find(
    (entry) => entry.value === category
  ) || {
    translationKey: "skorTicket",
    subjectPlaceholder: "Briefly describe your issue",
    messagePlaceholder:
      "Describe your question or issue and include any useful details.",
  };
  const requiresDocuments = DOCUMENT_REQUIRED_CATEGORIES.includes(category);
  const canSubmit =
    Boolean(category && subject.trim() && message.trim()) &&
    (!requiresDocuments ||
      Boolean(linkedTenantId && selectedDocuments.length)) &&
    !isSubmitting;

  useEffect(() => {
    if (tenant?.tenantDocs) setDocuments(tenant.tenantDocs);
  }, [tenant?._id]);

  useEffect(() => {
    if (
      !linkedTenantId ||
      (tenant?._id === linkedTenantId && tenant?.tenantDocs)
    ) {
      setLoadingDocuments(false);
      return;
    }
    const controller = new AbortController();
    setLoadingDocuments(true);
    setDocumentLoadFailed(false);
    axiosInstance
      .get(`${process.env.REACT_APP_SERVER_URL}/tenants/getUploadedDocuments`, {
        params: {
          tenantId: linkedTenantId,
          ...(isGuarantor ? { guarantor: true } : {}),
        },
        signal: controller.signal,
      })
      .then(({ data }) => {
        setDocuments(data?.success_res?.tenantDocs || []);
      })
      .catch((failure) => {
        if (failure.code !== "ERR_CANCELED") setDocumentLoadFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingDocuments(false);
      });
    return () => controller.abort();
  }, [linkedTenantId, isGuarantor]);

  const compulsoryDocNames = useMemo(
    () => (linkedTenantId ? getCompulsoryDocNames(tenant) : []),
    [linkedTenantId, tenant?.employment_status, tenant?.employment_category]
  );

  const documentNames = useMemo(
    () => [
      ...new Set([
        ...compulsoryDocNames,
        ...documents
          .map((document) => document?.type || document?.file_name)
          .filter(
            (name) =>
              name &&
              ![
                "uploaded-from-onboarding",
                "uploaded-from-onboardingg",
              ].includes(name.trim().toLowerCase())
          ),
      ]),
    ],
    [compulsoryDocNames, documents]
  );
  const documentOptions = documentNames.map((name) => ({
    value: name,
    label: _t(name, "", "fixed"),
  }));

  const addAttachments = (files) => {
    if (submitting.current || isSubmitting) return;
    const selectedFiles = Array.from(files || []);
    if (
      selectedFiles.some(
        (file) =>
          !ACCEPTED_FILE_TYPES.includes(file.type) ||
          !file.size ||
          file.size > MAX_FILE_SIZE
      ) ||
      attachments.length >= MAX_ATTACHMENTS ||
      attachments.length + selectedFiles.length > MAX_ATTACHMENTS
    ) {
      setFileError(true);
      return;
    }
    setFileError(false);
    setAttachments((current) => {
      const result = [...current];
      selectedFiles.forEach((file) => {
        if (result.length >= MAX_ATTACHMENTS) return;
        if (
          !result.some(
            (existing) =>
              existing.name === file.name &&
              existing.size === file.size &&
              existing.lastModified === file.lastModified
          )
        )
          result.push(file);
      });
      return result;
    });
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!category) {
      setError("category");
      return;
    }
    if (!subject.trim() || !message.trim()) {
      setError("required");
      return;
    }
    if (
      requiresDocuments &&
      (!linkedTenantId || selectedDocuments.length === 0)
    ) {
      setError("documents");
      return;
    }
    submitting.current = true;
    setIsSubmitting(true);
    setError("");
    try {
      await createSkorTicket({
        tenant_id: linkedTenantId,
        subject: subject.trim(),
        category,
        urgency,
        document_names: linkedTenantId
          ? selectedDocuments.map((document) => document.value)
          : [],
        message: message.trim(),
        attachments,
      });
      toast.success(_t("skorTicketCreated", "Your ticket has been submitted."));
      onClose();
    } catch {
      setError("submit");
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting.current) onClose();
  };

  return (
    <Dialog
      open
      onClose={closeModal}
      maxWidth={false}
      aria-labelledby="skor-ticket-title"
      aria-describedby="skor-ticket-description"
      PaperProps={{ className: styles.modal }}
    >
      <form onSubmit={submitTicket} className={styles.form}>
        <div className={styles.heading}>
          <h2 id="skor-ticket-title">
            {_t("skorTicketCreate", "Create ticket")}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            disabled={isSubmitting}
            onClick={closeModal}
            aria-label={_t("skorTicketClose", "Close")}
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <p id="skor-ticket-description" className={styles.description}>
          {_t(
            "skorTicketDescription",
            "Describe the issue. Our support team will review your ticket."
          )}
        </p>
        <fieldset disabled={isSubmitting} className={styles.fields}>
          <div className={styles.linkedRow}>
            <span>{_t("skorTicketLinkedTo", "Linked to")}</span>
            {linkedTenantId ? (
              <span className={styles.profileChip}>
                <FiUser aria-hidden="true" />
                <span>
                  {_t("skorTicketProfile", "Profile")} ·{" "}
                  {getUserDisplayName(tenant, "")}
                </span>
                {canUnlinkProfile && (
                  <button
                    type="button"
                    className={styles.removeButton}
                    aria-label={_t(
                      "skorTicketUnlinkProfile",
                      "Remove profile link and create a general ticket"
                    )}
                    onClick={() => {
                      setLinkedTenantId(null);
                      setSelectedDocuments([]);
                    }}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                )}
              </span>
            ) : (
              <>
                <span className={styles.profileChip}>
                  {canUnlinkProfile
                    ? _t(
                        "skorTicketGeneralWorkspace",
                        "General workspace ticket"
                      )
                    : _t("skorTicketGeneral", "General ticket")}
                </span>
                {tenant?._id && canUnlinkProfile && (
                  <button
                    type="button"
                    className={styles.relinkButton}
                    onClick={() => setLinkedTenantId(tenant._id)}
                  >
                    {_t("skorTicketLinkProfile", "Link profile")}
                  </button>
                )}
              </>
            )}
          </div>
          <label className={styles.field} htmlFor="skor-ticket-subject">
            <span>{_t("skorTicketSubject", "Subject")}</span>
            <input
              id="skor-ticket-subject"
              autoFocus
              required
              value={subject}
              placeholder={_t(
                `${categorySettings.translationKey}SubjectPlaceholder`,
                categorySettings.subjectPlaceholder
              )}
              onChange={(event) => setSubject(event.target.value)}
            />
          </label>
          <div className={styles.twoColumns}>
            <label className={styles.field} htmlFor="skor-ticket-category">
              <span>{_t("skorTicketCategory", "Category")}</span>
              <select
                id="skor-ticket-category"
                required
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                }}
              >
                <option value="" disabled>
                  {_t("skorTicketSelectCategory", "Select a category")}
                </option>
                {SKOR_TICKET_CATEGORIES.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {_t(entry.translationKey, entry.label)}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field} htmlFor="skor-ticket-urgency">
              <span>{_t("skorTicketUrgency", "Urgency")}</span>
              <select
                id="skor-ticket-urgency"
                value={urgency}
                onChange={(event) => setUrgency(event.target.value)}
              >
                <option value="high">{_t("skorTicketHigh", "High")}</option>
                <option value="medium">
                  {_t("skorTicketMedium", "Medium")}
                </option>
                <option value="low">{_t("skorTicketLow", "Low")}</option>
              </select>
            </label>
          </div>
          <div className={styles.field}>
            <label htmlFor="skor-ticket-documents">
              {requiresDocuments
                ? _t("skorTicketDocumentsRequired", "Documents (required)")
                : _t("skorTicketDocuments", "Documents (optional)")}
            </label>
            <Select
              className={styles.documentSelect}
              menuPlacement="bottom"
              inputId="skor-ticket-documents"
              isMulti
              isDisabled={isSubmitting || !linkedTenantId}
              isLoading={loadingDocuments}
              options={linkedTenantId ? documentOptions : []}
              value={selectedDocuments}
              onChange={(value) => setSelectedDocuments(value || [])}
              classNamePrefix="skor-ticket-select"
              placeholder={_t("skorTicketSelectDocuments", "Select documents")}
              noOptionsMessage={() =>
                _t("skorTicketNoDocuments", "No documents available")
              }
            />
            {documentLoadFailed && (
              <small className={styles.hint}>
                {_t(
                  "skorTicketDocumentsUnavailable",
                  "Documents could not be loaded. Close and reopen this form to try again."
                )}
              </small>
            )}
          </div>
          <label className={styles.field} htmlFor="skor-ticket-message">
            <span>{_t("skorTicketMessage", "Message")}</span>
            <textarea
              id="skor-ticket-message"
              required
              rows={4}
              value={message}
              placeholder={_t(
                `${categorySettings.translationKey}MessagePlaceholder`,
                categorySettings.messagePlaceholder
              )}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>
          <div
            className={`${styles.dropArea} ${isDragging ? styles.dragging : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!isSubmitting) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              addAttachments(event.dataTransfer.files);
            }}
          >
            <button
              type="button"
              className={styles.uploadButton}
              onClick={() => fileInput.current?.click()}
            >
              <FiPaperclip aria-hidden="true" />
              {_t(
                "skorTicketAddAttachments",
                "Attach screenshots or PDFs (optional), or drag them here."
              )}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,application/pdf"
              multiple
              hidden
              onChange={(event) => {
                addAttachments(event.target.files);
                event.target.value = "";
              }}
            />
          </div>
          {fileError && (
            <p className={styles.error} role="alert">
              {_t(
                "skorTicketInvalidFile",
                "Please select up to 5 non-empty PNG, JPG, or PDF files (max 10MB each)."
              )}
            </p>
          )}
          {attachments.length > 0 && (
            <ul className={styles.attachments}>
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className={styles.profileChip}
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className={styles.removeButton}
                    aria-label={_t(
                      "skorTicketRemoveAttachment",
                      "Remove {{name}}",
                      "translation",
                      { name: file.name }
                    )}
                    onClick={() =>
                      setAttachments((current) =>
                        current.filter((_, fileIndex) => fileIndex !== index)
                      )
                    }
                  >
                    <FiX aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
        {error && (
          <p className={styles.error} role="alert">
            {error === "category"
              ? _t("skorTicketCategoryRequired", "Please select a category.")
              : error === "documents"
                ? _t(
                    "skorTicketDocumentsRequiredError",
                    "Select at least one document for this category."
                  )
                : error === "required"
                  ? _t(
                      "skorTicketRequired",
                      "Please enter a subject and message."
                    )
                  : _t(
                      "skorTicketSubmitError",
                      "Your ticket could not be submitted. Please try again."
                    )}
          </p>
        )}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            disabled={isSubmitting}
            onClick={closeModal}
          >
            {_t("skorTicketCancel", "Cancel")}
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={!canSubmit}
            aria-busy={isSubmitting}
          >
            {isSubmitting
              ? _t("skorTicketSubmitting", "Submitting…")
              : _t("skorTicketSubmit", "Submit ticket")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
